import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
  ViewEncapsulation,
  inject,
} from '@angular/core';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { DashboardDetailsFacade } from '@backoffice-frontend/market/domain';
import { VarDirective } from '@backoffice-frontend/shared/directives/common';
import type { TimeFrame } from '@clean-code/models';

import {
  LineChartBaseChart,
  TimeSeriesFacade,
} from '@backoffice-frontend/time-series-identifier/domain';
import type { TimeSeriesInput } from '@clean-code/models';
import { indicate } from '@clean-code/shared/common';

import {
  DevExtremeLineChartComponent,
  type PointEventData,
  toLineChartViewModel,
} from '@clean-code/shared/components/ui-devextreme-charts';
import { TranslocoModule } from '@jsverse/transloco';
import { BehaviorSubject, Subject, combineLatest, concat, of } from 'rxjs';
import { map, startWith, switchMap } from 'rxjs/operators';

@Component({
    // eslint-disable-next-line @angular-eslint/component-selector
    selector: 'fof-index-chart',
    templateUrl: './index-chart.component.html',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CommonModule,
        MatProgressBarModule,
        TranslocoModule,
        DevExtremeLineChartComponent,
        VarDirective,
    ]
})
export class IndexChartComponent extends LineChartBaseChart<any> {
  private timeSeriesFacade = inject(TimeSeriesFacade);
  public marketDashboardDetailsFacade = inject(DashboardDetailsFacade);

  public isTableLoading$ = new BehaviorSubject<boolean>(false);

  @Output() deliveryDayChanged = new EventEmitter<boolean>();
  selectedPointData$ = new Subject<Date>();

  @Input()
  public set identifier(input: TimeSeriesInput) {
    this.identifier$.next(input);
  }

  @Input()
  public set timeFrame(value: TimeFrame) {
    this.selectedTimeFrame$.next(value);
  }

  @Input()
  public hideClosingText = false;

  @Input()
  public legend = false;

  public chartView$ = this.paramChange$.pipe(
    switchMap(([value, _, timeFrame]: [TimeSeriesInput, any, TimeFrame]) =>
      concat(
        of({ type: 'loading' } as const),
        this.timeSeriesFacade
          .getTimeSeriesChartData$([value.identifier], timeFrame)
          .pipe(
            indicate(this.isLoading$),
            map((series) => toLineChartViewModel(series)),
          ),
      ),
    ),
  );

  public detailsCompact$ = combineLatest([
    this.identifier$,
    this.selectedPointData$.pipe(startWith(null)),
  ]).pipe(
    switchMap(([value, date]: [TimeSeriesInput, Date]) =>
      this.marketDashboardDetailsFacade
        .getCompactIndexDetails$(value, date)
        .pipe(indicate(this.isTableLoading$)),
    ),
  );

  public details$ = combineLatest([
    this.identifier$,
    this.selectedPointData$.pipe(startWith(null)),
  ]).pipe(
    switchMap(([value, date]: [TimeSeriesInput, Date]) =>
      this.marketDashboardDetailsFacade
        .getIndexDetails$(value, date)
        .pipe(indicate(this.isTableLoading$)),
    ),
  );

  public calculationType$ = this.identifier$.pipe(
    map((x) => x.identifier.calculationType),
  );

  pointClickEvent($event: PointEventData): void {
    this.selectedPointData$.next($event.date);
  }
}
